import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import "../styles/StudentProfile.css";
import { userProfile } from "../api/userApi";
import { UserProfileModel } from "../types/user";

const StudentProfile: React.FC = () => {
  const { user } = useAuth();
  const imageUrl = process.env.REACT_APP_API_GET_IMAGE_URL || "";
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Mock profile data - in real app, this would come from API
  const [profileData, setProfileData] = useState<UserProfileModel>({
    studentCode: "",
    fullName: "",
    emailId: "",
    address: null,
    mobileNumber: "",
    dateOfJoining: "",
    isActive: true,
    remainingDays: 0,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const responseData = await userProfile(user?.email || "");
      setProfileData(responseData.data);

      localStorage.removeItem("user-data");

      const userData = {
        id: Date.now().toString(),
        email: responseData.data.emailId.trim(),
        role: "student" as const,
        name: responseData.data.fullName.trim(),
        avatar: responseData.data.fullName.charAt(0).toUpperCase(),
        studentId: responseData.data.studentCode,
        department: "Computer Science",
      };

      localStorage.setItem("user-data", JSON.stringify(userData));
    };
    fetchProfile();
  }, []);

  return (
    <div className="student-profile">
      {profileData.remainingDays !== undefined && profileData.remainingDays < 3 && (
        <div className="warning-banner">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>
            Warning: Your membership expires in {profileData.remainingDays} day{profileData.remainingDays !== 1 ? 's' : ''}. Please renew your membership soon!
          </span>
        </div>
      )}
      <div className="profile-header">
        <div
          className={`profile-avatar ${
            profileData.imagePath ? "clickable" : ""
          }`}
          onClick={() => profileData.imagePath && setIsImageModalOpen(true)}
        >
          {profileData.imagePath ? (
            <img
              src={imageUrl + profileData.imagePath}
              alt={profileData.fullName}
              className="profile-image"
            />
          ) : (
            <span className="profile-initials">
              {profileData.fullName?.charAt(0).toUpperCase() || "U"}
            </span>
          )}
        </div>
        <div className="profile-header-info">
          <h2>{profileData.fullName}</h2>
          <p className="profile-subtitle">
            Student ID: {profileData.studentCode}
          </p>
          <span
            className={`status-badge ${
              profileData.isActive ? "active" : "inactive"
            }`}
          >
            {profileData.isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="profile-content">
        <div className="profile-section">
          <h3 className="section-title">Personal Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Full Name</label>
              <p>{profileData.fullName}</p>
            </div>
            <div className="info-item">
              <label>Email Address</label>
              <p>{profileData.emailId}</p>
            </div>
            <div className="info-item">
              <label>Mobile Number</label>
              <p>{profileData.mobileNumber}</p>
            </div>
            <div className="info-item">
              <label>RFID</label>
              <p>
                {profileData.rfIdentificationCode === null
                  ? "XXXXXX"
                  : profileData.rfIdentificationCode}
              </p>
            </div>
            <div className="info-item">
              <label>Address</label>
              <p>{profileData.address}</p>
            </div>
            <div className="info-item">
              <label>Joining Date</label>
              <p>{new Date(profileData.dateOfJoining).toLocaleDateString()}</p>
            </div>
            <div className="info-item">
              <label>Remaining Days</label>
              <p>{profileData.remainingDays}</p>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h3 className="section-title">Seat Information</h3>
          <div className="seat-info-card">
            <div className="seat-number-display">
              <span className="seat-label">Current Seat</span>
              <span className="seat-value">
                {profileData.seatNumber || "Not Assigned"}
              </span>
            </div>
            {profileData.seatNumber && (
              <button
                className="view-seat-button"
                onClick={() => (window.location.hash = "sitting-arrangement")}
              >
                View on Map
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && profileData.imagePath && (
        <div
          className="image-modal-overlay"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="image-modal-close"
              onClick={() => setIsImageModalOpen(false)}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <img
              src={imageUrl + profileData.imagePath}
              alt={profileData.fullName}
              className="image-modal-img"
            />
            <p className="image-modal-name">{profileData.fullName}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProfile;
